/*
 * Collection facet enhancement.
 *
 * The <form> in sections/main-collection.liquid already works without this file: submitting it
 * navigates and every facet state lives in the URL. This script only removes the reload.
 *
 * Security invariants -- do not relax any of these:
 *   1. The fetch URL is derived from window.location, never from a DOM attribute, and is compared
 *      against window.location.origin before use. A URL read out of the DOM plus an innerHTML
 *      write is the standard DOM-XSS path.
 *   2. The `sections` parameter is checked against ALLOWED_SECTION_KEYS.
 *   3. The response is parsed with DOMParser and only the expected node is transplanted. There is
 *      no innerHTML assignment of response text anywhere in this file.
 */
(function () {
  'use strict';

  /* Section KEYS (the JSON-template key, e.g. templates/collection.json's "main"), not full ids.
   * A JSON-template section's actual id is auto-generated by Shopify as
   * "template--<template id>__<key>" (confirmed against the running dev server: fetching
   * ?sections=main returns {"main":null} -- no such section -- while the full derived id returns
   * the section's markup). The full id (captured below as sectionId) is validated against that
   * exact shape -- prefix, numeric template id, one of ALLOWED_SECTION_KEYS -- rather than just
   * checking the trailing key, so a crafted id ending in "__main" cannot slip through. The FULL
   * id is what is actually sent as the sections param and compared to the DOM wrapper -- so an
   * attacker still cannot steer this to an arbitrary section. */
  var ALLOWED_SECTION_KEYS = ['main'];

  var root = document.querySelector('[data-trf-collection]');
  if (!root) return;

  /* Two forms: the desktop rail and the drawer. They are deliberately separate -- one shared form
   * would put every checked facet into FormData twice (same name, same value) and duplicate every
   * filter in the URL. render() therefore always works from the form the event came from, and
   * apply() re-syncs the drawer from the response so the two never drift. */
  var forms = Array.prototype.slice.call(root.querySelectorAll('[data-trf-filter-form]'));
  var results = root.querySelector('[data-trf-results]');
  var live = root.querySelector('[data-trf-live]');
  if (!forms.length || !results) return;

  var sectionWrapper = root.closest('[id^="shopify-section-"]');
  var sectionId = sectionWrapper
    ? sectionWrapper.id.replace('shopify-section-', '')
    : null;
  var sectionIdPattern = new RegExp(
    '^(template|sections)--\\d+__(' + ALLOWED_SECTION_KEYS.join('|') + ')$'
  );
  if (!sectionId || !sectionIdPattern.test(sectionId)) return;

  var inFlight = null;

  /* Same-origin URL built from window.location plus the source form's own serialised state. */
  function buildUrl(form, includeSection) {
    var raw = new URLSearchParams(new FormData(form));
    /* Every param with an empty value (the price-range inputs when the customer has not typed a
     * floor/ceiling, and any other blank input) is dropped rather than serialised. Rebuilding
     * into a fresh URLSearchParams -- rather than calling .delete(key) on `raw` -- because delete
     * removes EVERY entry for a repeated key (checkbox facets share a name), not just the empty
     * one. Otherwise every "shareable, indexable" URL picks up e.g. filter.v.price.gte= with no
     * actual filter behind it. */
    var params = new URLSearchParams();
    raw.forEach(function (value, key) {
      if (value !== '') params.append(key, value);
    });
    var url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    url.search = params.toString();
    if (includeSection) url.searchParams.set('sections', sectionId);
    return url;
  }

  /* Focus is destroyed by the swap. Remember enough to put it back on the same control.
   *
   * The rail and drawer render the SAME name+value pairs, so an unscoped query always resolves
   * to the rail copy -- which is display:none below 990px, making .focus() a silent no-op that
   * drops focus out of an open drawer <dialog>. The originating form is threaded through so the
   * restore query is scoped to it. */
  function captureFocus(form) {
    var el = document.activeElement;
    if (!el || !el.name) return null;
    return { form: form, name: el.name, value: el.value };
  }

  function restoreFocus(token) {
    if (!token) return;
    var selector =
      '[name="' + CSS.escape(token.name) + '"][value="' + CSS.escape(token.value) + '"]';
    var el = token.form.querySelector(selector);

    /* `disabled` must be treated exactly like "not found". A facet whose post-swap count drops to
     * zero is re-rendered with the native disabled attribute (collection-facets.liquid), and a
     * disabled element silently refuses focus() -- so checking only for null let focus fall to
     * <body> with no error. That is the same class of silent failure this file was fixed for. */
    if (el && !el.disabled) {
      el.focus();
      return;
    }

    /* Nothing focusable matched inside the source form -- land on the results container rather
     * than dropping focus on the floor. Only the fallback gets a tabindex; never the facet input. */
    if (!results.hasAttribute('tabindex')) results.setAttribute('tabindex', '-1');
    results.focus();
  }

  function announce(fragment) {
    if (!live) return;
    var next = fragment.querySelector('[data-trf-live]');
    if (next) live.textContent = next.textContent;
  }

  function transplant(target, source) {
    /* A missing expected node is a broken contract, not a no-op -- throw so the caller's catch
     * can fall back to a real navigation instead of leaving the DOM half-updated. */
    if (!target || !source) throw new Error('transplant target or source missing');
    target.replaceChildren.apply(target, Array.prototype.slice.call(source.childNodes));
  }

  function apply(html, token) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = doc.querySelector('[data-trf-results]');
    if (!next) throw new Error('response missing [data-trf-results]');
    transplant(results, next);

    /* Re-sync the rail and the drawer from the response so their checked state matches the URL,
     * whichever one the customer actually used. */
    forms.forEach(function (form) {
      var selector = form.hasAttribute('data-trf-drawer-body')
        ? '[data-trf-drawer-body]'
        : '[data-trf-filter-form]:not([data-trf-drawer-body])';
      transplant(form, doc.querySelector(selector));
    });

    /* The sticky bar's active-filter badge, its "N of M" count, and the drawer's apply-button
     * label all live OUTSIDE [data-trf-results] and both forms, so the swap above never touches
     * them. Re-sync each from the parsed response so a filter change can never leave two
     * different counts on screen at once. */
    syncText('.collection-index__bar-count', doc);
    syncBadge('[data-trf-active-count]', doc);
    syncText('[data-trf-drawer-apply]', doc);

    announce(doc);
    restoreFocus(token);
  }

  function syncText(selector, doc) {
    var el = root.querySelector(selector);
    var next = doc.querySelector(selector);
    if (el && next) el.textContent = next.textContent;
  }

  function syncBadge(selector, doc) {
    var el = root.querySelector(selector);
    var next = doc.querySelector(selector);
    if (!el || !next) return;
    el.textContent = next.textContent;
    el.classList.toggle('is-empty', next.classList.contains('is-empty'));
  }

  function render(form, pushHistory) {
    var fetchUrl = buildUrl(form, true);
    var displayUrl = buildUrl(form, false);
    if (!fetchUrl || !displayUrl) return;

    var token = captureFocus(form);
    if (inFlight) inFlight.abort();
    inFlight = new AbortController();

    root.setAttribute('aria-busy', 'true');

    fetch(fetchUrl.toString(), { signal: inFlight.signal })
      .then(function (res) {
        if (!res.ok) throw new Error('status ' + res.status);
        return res.text();
      })
      .then(function (text) {
        /* The Section Rendering API's "sections" (plural) param -- unlike the older singular
         * "section_id" -- always answers with a JSON object keyed by section id, even for a
         * single section (confirmed against the running dev server: ?sections=<id> returns
         * {"<id>": "<html>"}), not the raw HTML DOMParser expects below. Unwrap it first. */
        var payload = JSON.parse(text);
        var html = payload[sectionId];
        if (typeof html !== 'string') throw new Error('missing section in response');
        apply(html, token);
        if (pushHistory) {
          window.history.pushState({ trfCollection: displayUrl.toString() }, '', displayUrl.toString());
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        /* Any failure falls back to the baseline: a real navigation. Never fail silently -- log
         * first so a genuine programming error (a TypeError from apply/transplant/restoreFocus/
         * announce, or a missing browser API) is distinguishable from a network failure instead
         * of looking like a silent success. */
        console.error('[trf-collection] falling back to navigation:', err);
        window.location.assign(displayUrl.toString());
      })
      .finally(function () {
        root.removeAttribute('aria-busy');
      });
  }

  forms.forEach(function (form) {
    form.addEventListener('change', function () {
      render(form, true);
    });
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      render(form, true);
    });
  });

  /* Chips and the clear link are plain <a> elements that already navigate correctly with no JS
   * involvement at all -- there is nothing here for an interceptor to enhance. A previous version
   * of this listener called preventDefault() unconditionally and then performed the exact same
   * navigation the bare <a> would have, which only cost every chip and "Limpiar filtros" its
   * ability to be opened in a new tab via Cmd/Ctrl-click or Shift-click. */

  window.addEventListener('popstate', function () {
    window.location.reload();
  });

  /* Drawer. <dialog> supplies the focus trap, Esc dismissal and focus return to the invoker --
   * that is exactly why it is a <dialog> and not a styled <div>. */
  var drawer = root.querySelector('[data-trf-drawer]');
  if (drawer && typeof drawer.showModal === 'function') {
    root.querySelectorAll('[data-trf-drawer-open]').forEach(function (button) {
      button.addEventListener('click', function () {
        drawer.showModal();
      });
    });
    drawer.querySelectorAll('[data-trf-drawer-close]').forEach(function (button) {
      button.addEventListener('click', function () {
        drawer.close();
      });
    });
    drawer.querySelectorAll('[data-trf-drawer-apply]').forEach(function (button) {
      button.addEventListener('click', function () {
        var drawerForm = drawer.querySelector('[data-trf-drawer-body]');
        drawer.close();
        if (drawerForm) render(drawerForm, true);
      });
    });
  }
})();
